import React from 'react'

import {
  AlignItems,
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Color,
  Display,
  JustifyContent,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import type { CampaignBannerRecipeBannerTagGroup } from '@library/api-menu-experience'

export type MerchandisingRecipeCardActionProps = {
  isInBasket: boolean
  isOutOfStock: boolean
  canAddRecipes: boolean
  reachedLimit: boolean
  onAddClick: (recipeId: string) => void
  onRemoveClick: (recipeId: string) => void
  onBlockedAddAttempt: () => void
  index?: number
}

export type MerchandisingRecipeCardProps<TRecipe extends { id: string; isAvailable: boolean }> = {
  recipe: TRecipe
  onClick: (recipeId: string) => void
} & MerchandisingRecipeCardActionProps

type RecipePricing = {
  perPortionSurcharge?: string
}

const ActionButton = withCitrusTheme(Button, (theme) => ({
  '&:disabled, &:disabled:hover, &:disabled:focus': {
    color: theme.color(Color.White),
  },
}))

export const CardTextFlow = withCitrusTheme(Box, () => ({
  '&::after': {
    content: '""',
    display: 'block',
    clear: 'both',
  },
}))

const FloatingButtonContainer = withCitrusTheme(Box, () => ({
  float: 'right',
  margin: '-1.5rem 0 0 0',
  width: '48px',
  height: '48px',
  borderRadius: '6px',
  backgroundColor: '#FFFFFF',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 2,
}))

const BannerTagStrip = withCitrusTheme(Box, () => ({
  padding: '0.25rem 0.75rem',
}))

export const twoLineTitleClampStyle: React.CSSProperties = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

export const MerchandisingBasketButton = ({
  recipeId,
  isInBasket,
  isOutOfStock,
  canAddRecipes,
  reachedLimit,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
}: {
  recipeId: string
} & MerchandisingRecipeCardActionProps) => {
  const atLimit = reachedLimit && !isInBasket
  const disabled = isOutOfStock || atLimit
  const handleKeyDown: React.KeyboardEventHandler = (event) => {
    event.stopPropagation()
  }

  const handleClick: React.MouseEventHandler = (event) => {
    event.stopPropagation()

    if (isInBasket) {
      onRemoveClick(recipeId)
      return
    }

    if (!canAddRecipes) {
      onBlockedAddAttempt()
      return
    }

    onAddClick(recipeId)
  }

  return (
    <FloatingButtonContainer onClick={(event) => event.stopPropagation()} onKeyDown={handleKeyDown}>
      <ActionButton
        variant={ButtonVariant.Icon}
        icon={isInBasket ? 'remove' : 'add'}
        colorVariant={isInBasket ? ButtonColorVariant.Secondary : ButtonColorVariant.Primary}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-testid="campaign-banner-basket-button"
        aria-label={isInBasket ? 'Remove recipe' : 'Add recipe'}
      />
    </FloatingButtonContainer>
  )
}

export const MerchandisingRecipeSurcharge = ({
  pricing,
  isOutOfStock,
}: {
  pricing?: RecipePricing
  isOutOfStock: boolean
}) => {
  const surchargeLabel = pricing?.perPortionSurcharge

  if (!surchargeLabel || isOutOfStock) {
    return null
  }

  return (
    <Box display={Display.Flex} data-testid="campaign-banner-recipe-surcharge" paddingV={2}>
      <Text size={2} color={Color.ColdGrey_800}>
        {surchargeLabel} per portion
      </Text>
    </Box>
  )
}

export const MerchandisingRecipeBannerTagStrip = ({
  banner,
}: {
  banner?: CampaignBannerRecipeBannerTagGroup
}) => {
  if (!banner || banner.tags.length === 0) {
    return null
  }

  return (
    <BannerTagStrip
      data-testid="campaign-banner-tag-strip"
      style={{
        backgroundColor: banner.colors.backgroundColor.light,
        color: banner.colors.color.light,
      }}
    >
      <Box
        display={Display.Flex}
        alignItems={AlignItems.Center}
        justifyContent={JustifyContent.SpaceEvenly}
        width="100%"
      >
        {banner.tags.map((tag, index) => (
          <React.Fragment key={tag.type}>
            {index > 0 ? (
              <Text aria-hidden="true" size={2} style={{ color: 'inherit' }}>
                |
              </Text>
            ) : null}
            <Text size={2} style={{ color: 'inherit' }}>
              {tag.text}
            </Text>
          </React.Fragment>
        ))}
      </Box>
    </BannerTagStrip>
  )
}
